import { TabType } from "../components/CreateNoteModal/AITemplateModal/types";
import { Template } from "../service/templates.service";

export type TProceduralCode = {
  id: number;
  code: string;
  short_description: string;
  rationale: string;
  additional_fields?: Record<string, any> | null;
};

export type TDiagnosticCode = {
  id: number;
  code: string;
  short_description: string;
  rationale: string;
  additional_fields?: Record<string, any> | null;
};

export type TNotes = {
  client_id?: number;
  name?: string;
  summary?: string;
  audio_id?: number | string;
  id: number;
  title: string;
  note_type: number;
  note_type_info?: {
    pk: number;
    id: number;
    name: string;
    document: string;
    finished_generating: boolean;
    settings: { is_addon: boolean; is_cpt_codes: boolean };
  };
  uploaded_at: string;
  assessment: string;
  error_status: number;
  category_type: number;
  language_type: number;
  status: number;
  is_read: boolean;
  stats_transcribe_start_at: string;
  stats_generate_note_start_at: string;
  modality_type: number;
  duration_seconds: number;
  subject: string;
  intake_note: string;
  eap_intake_note: string;
  psych_intake_note: string;
  psych_followup_note?: string;
  data: string;
  document?: string;
  emdr_note: string;
  avs_email: string;
  objective: string;
  plan: string;
  gender_type: number;
  client?: number;
  location_type?: number;
  duration_type?: number | null;
  duration_addon_type?: number;
  client_name?: string;
  session_insights?: string;
  stats_generate_note_end_at: string;
  treatment_plan?: string;
  sol_psych_intake_note?: string;
  sol_psych_followup_note?: string;
  sol_therapy_intake_note?: string;
  sol_therapy_followup_note?: string;
  psychotherapy_minutes?: number;
  psychotherapy_minutes_explanation?: string;
  suggested_cpt_type?: number;
  suggested_cpt_code_explanation?: string;
  is_incapture?: boolean;
  system_metadata?: {
    [key: string]: any;
  };
  procedural_codes: TProceduralCode[] | null;
  procedural_code_status: ProceduralCodeStatus;
  diagnostic_codes: TDiagnosticCode[] | null;
  diagnostic_code_status: DiagnosticCodeStatus;
  has_transcript?: boolean;
  is_processing_timed_out?: boolean;
  is_owned?: boolean;
};

export type Tdsm = {
  id: number;
  code: string;
  name: string;
};

export type TClient = {
  id: number;
  first_name: string;
  last_name: string;
};

export type TRecordingDetailsType = {
  title: string;
  note_type?: number;
  category_type: number;
  modality_type: string;
  gender_type: number;
  language_type: number;
  location_type?: number;
  duration_type?: number | null;
  duration_addon_type?: number;
  client_id?: number;
  description?: string;
  fileList?: any[];
  is_template?: boolean;
  template_id?: number;
  current_recording_id?: number;
};

export interface AppState {
  isCreateClientDrawerVisible: boolean;
  isCustomiseSectionVisible: boolean;
  selectedClient: any;
  uploadInProgress: boolean;
  isEditNote: boolean;
  isCreateModalVisible: boolean;
  isAITemplateModalVisible: boolean;
  isSubmissionInProgress: boolean;
  selectedNoteForEditing: any;
  isNotesViewVisible: boolean;
  uploadType: number;
  showRecordingView: boolean;
  loadingNotes: boolean;
  currentPage: number;
  isDemoPath: boolean;
  isMobilePath: boolean;
  openShare: boolean;
  showDemoSignupModal: boolean;
  addClientView: boolean;
  audioDevices: any[];
  customTemplateSelectedTab: TabType;
  demoState: {
    isClientModalOpen: boolean;
    isNoteModalOpen: boolean;
    clientForm: any;
    recordingDetails: any;
  };
  templateOpenedFromNoteModal: boolean;
  storedTemplateData: {
    id: number;
    pk?: number;
    name: string;
    document: string;
    prompt?: string;
    isFromTemplate?: boolean;
    appendInstructions?: string;
    focusInstructions?: boolean;
  } | null;
  templates: Template[];
  selectedView: string;
  showRestrictionModal: boolean;
  showSettingsModal: boolean;
  shareClientModalVisible: boolean;
  shareClientModalClient: TClientForm | null;
}

export type TAudioFileDocument = {
  id: number;
  filename: string;
  file_path: string;
  file_size: number;
  uploaded_at: string;
};

export type TClientForm = {
  id?: number;
  uuid?: string;
  name: string;
  category_type: number;
  gender_type: number;
  language_type: number;
  diagnosis_ids: number[];
  refer_type?: number;
  age?: number | string;
  mrn?: string | null;
  date_of_birth?: string | null;
  context?: string;
  progress_summary?: TProgressSummary;
  progress_summary_last_updated_at?: string;
  diagnoses?: Tdsm[];
  is_owned?: boolean;
  owner_name?: string;
  owner_email?: string;
};

export type TClientShare = {
  id: number;
  client: number;
  shared_with: number;
  shared_with_name: string;
  shared_with_email: string;
  created_at: string;
};

export type TClientListResponse = {
  owned: TClientForm[];
  shared: TClientForm[];
};

export type TProgressSummary = {
  next_steps: string;
  primary_concern: string;
  session_summary: string;
};

export enum ProceduralCodeStatus {
  PENDING = 0,
  COMPLETE = 1,
  NOT_PROCESSED = 2,
  FAILED = 3,
}

export enum DiagnosticCodeStatus {
  PENDING = 0,
  COMPLETE = 1,
  NOT_PROCESSED = 2,
  FAILED = 3,
}

export enum ModalityMode {
  audio = 0,
  dictate = 1,
  write = 2,
  upload = 3,
}

// TypeScript interfaces for onboarding form data
export interface OnboardingFormData {
  practice_area: string; // code
  specialty: string; // code
  referral_source: string;
  ehr: string;
  work_as: string;
}

export interface OnboardingData {
  specialty?: string; // for BE extraction compatibility
  referral_source?: string;
  ehr?: string;
  work_as?: string;
  completed?: boolean;
  start_time?: string;
  timezone?: string;
}

// EHR Transfer Types
export type EHRTransferStatus =
  | "not_started"
  | "pending"
  | "logging_in"
  | "awaiting_mfa"
  | "patient_lookup"
  | "awaiting_appointment"
  | "template_mapping"
  | "writing_note"
  | "completed"
  | "failed";

export type AppointmentOption = {
  id: string;
  date: string;
  time: string;
  visit_type: string;
  provider?: string;
  status?: string;
  location?: string;
  duration?: string;
  [key: string]: string | undefined;
};

export type TransferJob = {
  id: string;
  status: EHRTransferStatus;
  patient_name: string;
  error_message?: string;
  requires_appointment_selection: boolean;
  requires_mfa_code?: boolean;
  appointment_options?: AppointmentOption[];
  browserbase_session_url?: string;
  created_at?: string;
  updated_at?: string;
  completed_at?: string;
};

export type EHRCredentialCheckResponse = {
  has_credential: boolean;
  credential_type?: "user" | "group";
  ehr_provider?: string;
  ehr_provider_display?: string;
};

export type StartEHRTransferResponse = {
  success: boolean;
  job_id?: string;
  status?: EHRTransferStatus;
  task_id?: string;
  error?: string;
  code?: string;
};

export type EHRTransferStatusResponse = {
  success: boolean;
  job?: TransferJob | null;
  status?: EHRTransferStatus;
  error?: string;
};
